import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useTranslation } from 'react-i18next'
import { SIDEBAR_WIDTH } from '@/components/researcher/ResearcherSidebar'

type Props = {
  count: number
  onGroup: (anchor: HTMLElement) => void
  onDelete: () => void
  onInvite: () => void
  onClear: () => void
}

/**
 * Floating glass bar for bulk actions. Stays mounted so it can slide out the
 * way it came in; hidden bars are inert.
 */
export default function SelectionBar({ count, onGroup, onDelete, onInvite, onClear }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('respondents')
  const open = count > 0

  const ghost = {
    height: 36,
    px: 1.75,
    borderRadius: '999px',
    fontWeight: 700,
    fontSize: 13.5,
    color: '#fff',
    bgcolor: alpha('#fff', 0.1),
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: alpha('#fff', 0.18) },
  } as const

  return (
    <Box
      role="toolbar"
      aria-hidden={!open}
      inert={!open || undefined}
      sx={{
        position: 'fixed',
        zIndex: theme.zIndex.appBar + 1,
        bottom: { xs: 16, md: 28 },
        left: { xs: '50%', md: `calc(50% + ${SIDEBAR_WIDTH / 2}px)` },
        maxWidth: 'calc(100vw - 32px)',
        transform: open ? 'translate(-50%, 0)' : 'translate(-50%, calc(100% + 40px))',
        opacity: open ? 1 : 0,
        // Critically damped feel: quick out of the gate, no overshoot.
        transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease-out',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        p: '8px 8px 8px 20px',
        borderRadius: '999px',
        color: '#fff',
        bgcolor: alpha(c.navy, 0.86),
        backdropFilter: 'blur(24px) saturate(180%)',
        boxShadow: `0 12px 32px ${alpha(c.navy, 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.12)}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'opacity 150ms ease', transform: 'translate(-50%, 0)' },
        '@media (prefers-reduced-transparency: reduce)': { bgcolor: c.navy, backdropFilter: 'none' },
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, fontSize: 14, mr: 1, whiteSpace: 'nowrap' }}>
        {t('selection.count', { count })}
      </Box>
      <Button onClick={(e) => onGroup(e.currentTarget)} sx={{ ...ghost, display: { xs: 'none', sm: 'inline-flex' } }}>
        {t('selection.toGroup')}
      </Button>
      <Button onClick={onDelete} sx={ghost}>
        {t('selection.delete')}
      </Button>
      <Button
        variant="contained"
        onClick={onInvite}
        sx={{ height: 36, px: 2, borderRadius: '999px', fontWeight: 700, fontSize: 13.5, whiteSpace: 'nowrap' }}
      >
        {t('selection.invite')}
      </Button>
      <Tooltip title={t('selection.clear')}>
        <IconButton onClick={onClear} size="small" sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff' } }}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}
